"use client";

import { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";

interface HowItWorksVideoProps {
  src: string;
  poster: string;
  label: string;
  pauseLabel: string;
  playLabel: string;
}

const VISIBLE_RATIO = 0.25;

/**
 * The 10-second "Scan. Tap. Done." loop. It loads once it nears the viewport
 * and plays only while visible. Visitors who prefer reduced motion or have
 * data saving on get the poster until they press play, and everyone can pause
 * it (an endless loop needs a pause control) or start it when the browser
 * blocks autoplay.
 */
export default function HowItWorksVideo({
  src,
  poster,
  label,
  pauseLabel,
  playLabel,
}: HowItWorksVideoProps): React.JSX.Element {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [shouldLoad, setShouldLoad] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [canAutoplay, setCanAutoplay] = useState(false);
  const [userWantsPlay, setUserWantsPlay] = useState<boolean | null>(null);
  const wantsPlay = userWantsPlay ?? canAutoplay;

  useEffect(() => {
    const query = window.matchMedia("(prefers-reduced-motion: reduce)");
    const isSavingData =
      (navigator as Navigator & { connection?: { saveData?: boolean } })
        .connection?.saveData === true;
    const update = (): void => setCanAutoplay(!query.matches && !isSavingData);
    update();
    query.addEventListener("change", update);
    return () => query.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    const element = containerRef.current;
    if (!element) {
      return;
    }

    if (typeof IntersectionObserver === "undefined") {
      setShouldLoad(true);
      setIsVisible(true);
      return;
    }

    const nearObserver = new IntersectionObserver(
      ([entry]) => {
        if (entry?.isIntersecting) {
          setShouldLoad(true);
          nearObserver.disconnect();
        }
      },
      { rootMargin: "360px 0px" }
    );
    const visibleObserver = new IntersectionObserver(
      ([entry]) =>
        setIsVisible(
          entry?.isIntersecting === true &&
            entry.intersectionRatio >= VISIBLE_RATIO
        ),
      { threshold: [0, VISIBLE_RATIO] }
    );

    nearObserver.observe(element);
    visibleObserver.observe(element);

    return () => {
      nearObserver.disconnect();
      visibleObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoad) {
      return;
    }

    if (isVisible && wantsPlay) {
      void video.play().catch(() => {
        // Autoplay can be blocked; the play button stays available.
      });
    } else {
      video.pause();
    }
  }, [isVisible, shouldLoad, wantsPlay]);

  const toggle = (): void => {
    const video = videoRef.current;
    if (!video) {
      return;
    }

    setUserWantsPlay(!isPlaying);
    if (isPlaying) {
      video.pause();
    } else {
      // Called inside the click so browsers that blocked autoplay allow it.
      void video.play().catch(() => {});
    }
  };

  return (
    <div ref={containerRef} className="relative h-full w-full">
      <video
        ref={videoRef}
        aria-label={label}
        className="h-full w-full object-cover"
        poster={poster}
        src={shouldLoad ? src : undefined}
        disablePictureInPicture
        disableRemotePlayback
        loop
        muted
        playsInline
        preload={shouldLoad && wantsPlay ? "auto" : "none"}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
        // A missing or undecodable file never fires "pause", so reset here.
        onError={() => setIsPlaying(false)}
      />
      <button
        type="button"
        onClick={toggle}
        aria-label={isPlaying ? pauseLabel : playLabel}
        className="absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-[#241c15]/80 text-white backdrop-blur transition-colors hover:bg-[#241c15] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#241c15]"
      >
        {isPlaying ? (
          <Pause aria-hidden="true" className="h-4 w-4" />
        ) : (
          <Play aria-hidden="true" className="ml-0.5 h-4 w-4" />
        )}
      </button>
    </div>
  );
}
